'use client';

import { mapErrorResponseIntoForm } from '@lib/form/form-validation';
import * as RadixForm from '@radix-ui/react-form';
import { revalidateLogic, useForm, useStore } from '@tanstack/react-form';
import { Alert } from '@tuic/web-compositions/core/Alert';
import { Button } from '@tuic/web-compositions/core/Button';
import { Divider } from '@tuic/web-compositions/core/Divider';
import { Pictogram } from '@tuic/web-compositions/core/Pictogram';
import { Skeleton } from '@tuic/web-compositions/core/Skeleton';
import { InteractionHeader } from '@tuic/web-compositions/my-trips/InteractionHeader';
import { ManifestPinDialog } from '@tuic/web-compositions/my-trips/ManifestPinDialog';
import { AlertDialog } from '@tuic/web-compositions/shared/AlertDialog';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import {
  Fragment,
  useEffect,
  useMemo,
  useState,
  useTransition,
} from 'react';

import { ContactSection } from './sections/ContactSection';
import { IdCardSection } from './sections/IdCardSection';
import { OtherInformationSection } from './sections/OtherInformationSection';
import { PersonalInfoSection } from './sections/PersonalInfoSection';
import { createManifestSchema } from './validation';

import type {
  Manifest,
  ManifestFieldErrors,
  ManifestFormValues,
} from './types';
import type { SimpleFormApi } from '@lib/form/helper';
import type {
  I18NFromComponent,
  I18NInterface,
} from '@tuic/web-compositions/utils/i18n';
import type { FC, FormEvent, ReactNode } from 'react';
import type { ZodIssue } from 'zod';

// Skeleton row layout per section — keep in sync with the real sections
// (PersonalInfoSection: 2 rows × 2 fields; ContactSection: 1+2+2+2+checkbox; …).
type SkeletonRow = number | 'checkbox';

const SECTION_LAYOUTS: SkeletonRow[][] = [
  [2, 2], // PersonalInfo
  [1, 2, 2, 2, 'checkbox'], // Contact
  [2, 2, 2, 2, 2, 'checkbox', 'checkbox'], // IdCard
  [2, 2, 1, 2, 'checkbox', 'checkbox', 1], // OtherInformation
];

const ManifestSectionSkeletonRow: FC<{ row: SkeletonRow }> = ({ row }) => {
  if (row === 'checkbox') {
    return <Skeleton className="h-5 w-3/4" />;
  }
  return (
    <div className="flex flex-col gap-4 md:flex-row md:gap-8">
      {Array.from({ length: row }, (_, i) => (
        <Skeleton key={i} className="h-[58px] w-full rounded-md" />
      ))}
    </div>
  );
};

const ManifestSectionSkeleton: FC<{ rows: SkeletonRow[] }> = ({ rows }) => (
  <section className="space-y-4">
    <div className="flex items-center gap-4">
      <Skeleton className="h-10 w-10 rounded-full" />
      <Skeleton className="h-7 w-1/3 max-w-[280px]" />
    </div>
    <div className="flex flex-col gap-6">
      {rows.map((row, i) => (
        <ManifestSectionSkeletonRow key={i} row={row} />
      ))}
    </div>
  </section>
);

const ManifestFormFallback: FC = () => (
  <div className="space-y-10 text-marine-high-emphasis">
    <div className="space-y-4">
      <Skeleton className="h-9 w-1/2 max-w-[400px]" />
      <div className="space-y-2">
        <Skeleton className="h-5 w-full" />
        <Skeleton className="h-5 w-5/6" />
      </div>
    </div>

    <div className="space-y-8">
      {SECTION_LAYOUTS.map((rows, i) => (
        <Fragment key={i}>
          <ManifestSectionSkeleton rows={rows} />
          {i < SECTION_LAYOUTS.length - 1 && <Divider align="horizontal" />}
        </Fragment>
      ))}

      <div className="flex items-center justify-between pt-4">
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-11 w-32 rounded-md" />
      </div>
    </div>
  </div>
);

export type ManifestFormProps = {
  fallback?: boolean;

  headline?: ReactNode;
  subline?: ReactNode;
  pinDialogHeadline?: ReactNode;
  pinDialogSubline?: ReactNode;

  manifest?: Manifest;

  /** First trip's startDate (ISO YYYY-MM-DD). Enables `dateOfIssue < embark`. */
  embarkDate?: string;
  /** Last trip's endDate (ISO YYYY-MM-DD). Enables `validUntil ≥ debark + N months`. */
  debarkDate?: string;

  /** True for UK cruises — drives the conditional ETA-confirmation checkbox. */
  ukCruise?: boolean;

  /** True when the booking has only one traveller — drives the custom
   * header's back-button to skip the (auto-redirecting) overview. */
  singleTravellerBooking?: boolean;

  entryRequirementsHref?: string;

  /** Called when the full form is submitted */
  onSubmit?: (values: ManifestFormValues) => void;

  /** Whether a submission is currently in progress */
  isSubmitting?: boolean;

  /** Field-level errors from the API (keyed by section then field) */
  fieldErrors?: ManifestFieldErrors | null;

  i18n?: I18NFromComponent<typeof PersonalInfoSection> &
    I18NFromComponent<typeof ContactSection> &
    I18NFromComponent<typeof IdCardSection> &
    I18NFromComponent<typeof OtherInformationSection> &
    I18NFromComponent<typeof ManifestPinDialog> &
    I18NInterface<
      | 'manifestFieldErrorsAlert'
      | 'manifestPreliminaryHeadline'
      | 'manifestPreliminaryWarning'
      | 'formSave'
      | 'formRequiredNote'
      | 'formBack'
      | 'formTryAgain'
      | 'manifestValidationFirstNameRequired'
      | 'manifestValidationLastNameRequired'
      | 'manifestValidationStreetAddressRequired'
      | 'manifestValidationZipCodeRequired'
      | 'manifestValidationCityRequired'
      | 'manifestValidationCountryRequired'
      | 'manifestValidationPassportTypeRequired'
      | 'manifestValidationNationalityRequired'
      | 'manifestValidationPassportNumberRequired'
      | 'manifestValidationPlaceOfBirthRequired'
      | 'manifestValidationPlaceOfIssueRequired'
      | 'manifestValidationDateOfBirthRequired'
      | 'manifestValidationDateOfIssueRequired'
      | 'manifestValidationValidUntilRequired'
      | 'manifestValidationProfessionRequired'
      | 'manifestValidationPreferredLanguageRequired'
      | 'manifestValidationEmergencyNameRequired'
      | 'manifestValidationEmergencyPhoneRequired'
      | 'manifestValidationDateOfBirthPast'
      | 'manifestValidationDateOfBirthBeforeEmbark'
      | 'manifestValidationDateOfBirthAdultMinAge'
      | 'manifestValidationDateOfBirthChildAge'
      | 'manifestValidationDateOfBirthBabyAge'
      | 'manifestValidationDateOfBirthMaxAge'
      | 'manifestValidationValidUntilFuture'
      | 'manifestValidationDateOfIssueBeforeEmbark'
      | 'manifestValidationZipCodeFormatDE'
      | 'manifestValidationZipCodeFormatATCH'
      | 'manifestValidationPhoneFormat'
      | 'manifestValidationMobileFormat'
      | 'manifestValidationPassportNumberFormat'
      | 'manifestValidationApproveMobilePhoneDisclosureMustAccept'
      | 'manifestValidationEntryRequirementsMustAccept'
      | 'manifestValidationHealthTermsMustAccept'
      | 'manifestValidationDataProtectionMustAccept'
      | 'manifestValidationHasETAMustAccept'
      | 'manifestValidationVisaMustAccept'
      | 'manifestValidationPregnant24WeeksMustAccept'
      | 'manifestValidationDateInvalid'
      | 'manifestValidationEmailInvalid'
      | 'manifestValidationValidUntilAfterIssue'
      | 'manifestValidationValidUntilAfterEmbark'
      | 'contactAddressHintText'
      | 'contactAddressHintConfirm'
      | 'contactAddressHintCancel'
    >;

  isPinRequired?: boolean;
  onPinEnter?: (pin: string) => boolean;
};

const buildInitialValues = (manifest?: Manifest): ManifestFormValues => {
  const data = manifest?.manifestData;
  const personal = data?.personalInfo;
  const contact = data?.contact;
  const credentials = data?.credentials;
  const other = data?.otherInformation;
  const dietary = other?.dietary;
  const emergency = other?.emergencyContact;

  return {
    personalInfo: {
      salutation: personal?.salutation ?? '',
      title: personal?.title ?? '',
      firstName: personal?.firstName ?? '',
      lastName: personal?.lastName ?? '',
    },
    contact: {
      streetAddress: contact?.streetAddress ?? '',
      zipCode: contact?.zipCode ?? '',
      city: contact?.city ?? '',
      country: contact?.country ?? '',
      email: contact?.email ?? '',
      phone: contact?.phone ?? '',
      mobile: contact?.mobile ?? '',
      approveMobilePhoneDisclosure:
        contact?.approveMobilePhoneDisclosure ?? false,
    },
    credentials: {
      passportType: credentials?.passportType ?? '',
      nationality: credentials?.nationality ?? '',
      passportNumber: credentials?.passportNumber ?? '',
      placeOfBirth: credentials?.placeOfBirth ?? '',
      placeOfIssue: credentials?.placeOfIssue ?? '',
      dateOfBirth: credentials?.dateOfBirth ?? '',
      dateOfIssue: credentials?.dateOfIssue ?? '',
      birthName: credentials?.birthName ?? '',
      validUntil: credentials?.validUntil ?? '',
      profession: credentials?.profession ?? '',
      entryRequirementsAccepted: credentials?.entryRequirementsAccepted ?? false,
      hasVisa: credentials?.hasVisa ?? false,
      hasETA: credentials?.hasETA ?? false,
    },
    otherInformation: {
      mealRequest: dietary?.mealRequest ?? '',
      individualRequest: dietary?.individualRequest ?? '',
      allergy: dietary?.allergy ?? [],
      allergyInfo: dietary?.allergyInfo ?? '',
      handicap: other?.handicap ?? '',
      emergencyContactName: emergency?.name ?? '',
      emergencyContactPhone: emergency?.phone ?? '',
      acceptDataProtectionTerms: other?.acceptDataProtectionTerms ?? false,
      acceptHealthTerms: other?.acceptHealthTerms ?? false,
      pregnant24Weeks: other?.pregnant24Weeks ?? false,
      preferredLanguageCode: data?.preferredLanguageCode ?? '',
    },
  };
};

// A street address without any digit is almost certainly missing a house
// number — we don't hard-reject it (some rural / international addresses
// really have no number), but we ask the user to confirm before submitting.
const addressMissingHouseNumber = (street: string): boolean =>
  !!street && !/\d/.test(street);

// Flatten `{ section: { field: msgs } }` to ZodIssue[] with dotted paths
// matching our TanStack field names (`personalInfo.salutation`, …).
const flattenFieldErrors = (
  fieldErrors: ManifestFieldErrors | null | undefined,
): ZodIssue[] => {
  if (!fieldErrors) return [];
  const issues: ZodIssue[] = [];
  for (const [section, sectionErrors] of Object.entries(fieldErrors)) {
    if (!sectionErrors || typeof sectionErrors !== 'object') continue;
    if (Array.isArray(sectionErrors)) {
      for (const message of sectionErrors) {
        issues.push({ code: 'custom', path: [section], message });
      }
      continue;
    }
    for (const [field, messages] of Object.entries(sectionErrors)) {
      if (!Array.isArray(messages)) continue;
      for (const message of messages) {
        issues.push({
          code: 'custom',
          path: [`${section}.${field}`],
          message,
        });
      }
    }
  }
  return issues;
};

const ManifestFormHeader: FC<{
  travellerName?: string;
  singleTravellerBooking?: boolean;
  backLabel?: string;
}> = ({ travellerName, singleTravellerBooking, backLabel }) => {
  const pathname = usePathname() ?? '';
  const upLevels = singleTravellerBooking ? 2 : 1;
  const backHref =
    '/' +
    pathname.split('/').filter(Boolean).slice(0, -upLevels).join('/');

  return (
    <div className="sticky top-0 z-10 mb-8 bg-section-white md:mb-4 xl:mb-8">
      <InteractionHeader
        title={travellerName ?? <Skeleton className="h-2 w-[150px]" />}
        leftButton={{
          iconName: 'arrow-left',
          variant: 'secondary',
          label: backLabel,
          children: <Link href={backHref || '/'} />,
        }}
      />
    </div>
  );
};

export const ManifestForm: FC<ManifestFormProps> = props => {
  const travellerName =
    `${props.manifest?.manifestData?.personalInfo?.firstName ?? ''} ${props.manifest?.manifestData?.personalInfo?.lastName ?? ''}`.trim();

  if (props.fallback) {
    return (
      <>
        <div className="flex justify-center w-full h-[64px] py-2 md:py-4 mb-12">
          <div className="relative flex-1 max-w-[1312px]">
            <div className="absolute left-0 top-1/2 -translate-y-1/2">
              <Skeleton className="rounded-full size-[48px] md:size-[36px]" />
            </div>
            <div className="px-4 h-full flex items-center justify-center text-center overflow-hidden mx-[calc(1_*_48px_+_2_*_0.5rem)] md:mx-[36px]">
              <Skeleton className="h-2 w-[150px]" />
            </div>
          </div>
        </div>
        <ManifestFormFallback />
        {props.isPinRequired && (
          <ManifestPinDialog
            open
            closeable={false}
            headline={props.pinDialogHeadline}
            subline={props.pinDialogSubline}
            onPinEnter={props.onPinEnter}
            travellerName={travellerName}
            i18n={props.i18n}
          />
        )}
      </>
    );
  }
  return (
    <>
      <ManifestFormHeader
        travellerName={travellerName || undefined}
        singleTravellerBooking={props.singleTravellerBooking}
        backLabel={props.i18n?.formBack}
      />
      <ManifestFormContent {...props} />
    </>
  );
};

const ManifestFormContent: FC<ManifestFormProps> = ({
  headline,
  subline,
  pinDialogHeadline,
  pinDialogSubline,
  onPinEnter,
  onSubmit,
  isSubmitting,
  fieldErrors,
  i18n,
  isPinRequired,
  manifest,
  embarkDate,
  debarkDate,
  ukCruise,
  entryRequirementsHref,
}) => {
  const salutation = manifest?.manifestData?.personalInfo?.salutation ?? '';

  const schema = useMemo(
    () =>
      createManifestSchema(i18n, {
        embarkDate,
        debarkDate,
        salutation,
        ukCruise,
      }),
    [i18n, embarkDate, debarkDate, salutation, ukCruise],
  );

  // Address-hint dialog: when the schema passes but the street has no digits,
  // we park the validated values here and open the dialog. "Bestätigen" fires
  // the parent onSubmit with the parked values; "Bearbeiten" just closes.
  const [pendingSubmit, setPendingSubmit] = useState<ManifestFormValues | null>(
    null,
  );

  const form = useForm({
    defaultValues: buildInitialValues(manifest),
    // Classic "onSubmit first, then revalidate onChange" UX via TanStack's
    // built-in `revalidateLogic` — matches React Hook Form's default mode.
    validationLogic: revalidateLogic({
      mode: 'submit',
      modeAfterSubmission: 'change',
    }),
    validators: { onDynamic: schema },
    onSubmit: ({ value }) => {
      if (addressMissingHouseNumber(value.contact.streetAddress)) {
        setPendingSubmit(value);
        return;
      }
      onSubmit?.(value);
    },
  });

  // Merge server-side field errors into TanStack form meta when they change.
  useEffect(() => {
    const issues = flattenFieldErrors(fieldErrors);
    if (issues.length === 0) return;
    mapErrorResponseIntoForm(
      { success: false, message: 'validation failed', fieldErrors: issues },
      form,
    );
  }, [fieldErrors, form]);

  const fullName =
    `${manifest?.manifestData?.personalInfo?.firstName ?? ''} ${manifest?.manifestData?.personalInfo?.lastName ?? ''}`.trim();

  const isPreliminary = manifest?.preliminary === true;
  const isDisabled = isSubmitting;

  const router = useRouter();
  const [isRefetching, startRefetchTransition] = useTransition();

  const handleTryAgain = (): void => {
    startRefetchTransition(() => {
      router.refresh();
    });
  };

  // Open field-level errors after ≥1 submit attempt.
  // Auto-clears once all are fixed. Covers client + server-mapped field errors.
  const hasValidationErrors = useStore(form.store, state => {
    if ((state.submissionAttempts ?? 0) === 0) return false;
    return Object.values(state.fieldMeta ?? {}).some(
      meta => ((meta as { errors?: unknown[] })?.errors?.length ?? 0) > 0,
    );
  });

  const handleFormSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    event.stopPropagation();
    const formEl = event.currentTarget;
    void form.handleSubmit().then(() => {
      const fieldMeta = form.state.fieldMeta as Record<
        string,
        { errors?: unknown[] } | undefined
      >;
      const erroredFields = Object.entries(fieldMeta ?? {})
        .filter(([, meta]) => (meta?.errors?.length ?? 0) > 0)
        .map(([name]) => name);
      if (erroredFields.length === 0) return;

      // Scroll to and focus the first field (in DOM order) that has a
      // validation error. Some primitives (Select, PhoneNumberInput, Birthdate)
      // render a hidden `<input name=X>` alongside the visible controls —
      // we skip hidden matches and fall back to the nearest visible ancestor.
      const errored = new Set(erroredFields);
      const isVisible = (el: HTMLElement): boolean =>
        el.offsetParent !== null &&
        !(el instanceof HTMLInputElement && el.type === 'hidden');

      const candidates = Array.from(
        formEl.querySelectorAll<HTMLElement>('[name]'),
      ).filter(el => errored.has(el.getAttribute('name') ?? ''));

      const visible = candidates.find(isVisible);
      if (visible) {
        visible.scrollIntoView({ behavior: 'smooth', block: 'center' });
        visible.focus();
        return;
      }

      // All matches were hidden — walk up from the first match to a visible
      // ancestor and focus the first visible input inside it.
      const hidden = candidates[0];
      if (!hidden) return;
      let cursor: HTMLElement | null = hidden;
      while (cursor && !isVisible(cursor) && cursor !== formEl) {
        cursor = cursor.parentElement;
      }
      if (cursor && cursor !== formEl) {
        cursor.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const focusable = cursor.querySelector<HTMLElement>(
          'input:not([type=hidden]), textarea, button',
        );
        focusable?.focus();
      }
    });
  };

  // Cast to the shared type used by section props — bypasses TanStack's
  // 12-generic `ReactFormExtendedApi` at the component boundary.
  const sectionForm = form as unknown as SimpleFormApi<ManifestFormValues>;

  if (isPreliminary) {
    return (
      <div className="my-16 flex flex-col items-center gap-6 text-center text-marine-high-emphasis">
        <Pictogram name="important" size="md" />
        <h1 className="headline-lg">{i18n?.manifestPreliminaryHeadline}</h1>
        <div className="text-lg">{i18n?.manifestPreliminaryWarning}</div>
        <Button
          onClick={handleTryAgain}
          indicating={isRefetching}
          disabled={isRefetching}
        >
          {i18n?.formTryAgain}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-10 text-marine-high-emphasis">
      <div className="space-y-4">
        {headline && <h1 className="headline-lg">{headline}</h1>}
        {subline && <div className="text-lg">{subline}</div>}
      </div>

      {hasValidationErrors && (
        <Alert mode="error" headline={i18n?.manifestFieldErrorsAlert} />
      )}

      <RadixForm.Root
        onSubmit={handleFormSubmit}
        className="space-y-8"
        noValidate
      >
        <PersonalInfoSection
          form={sectionForm}
          manifest={manifest}
          disabled={isDisabled}
          i18n={i18n}
        />
        <Divider align="horizontal" />
        <ContactSection
          form={sectionForm}
          manifest={manifest}
          disabled={isDisabled}
          i18n={i18n}
        />
        <Divider align="horizontal" />
        <IdCardSection
          form={sectionForm}
          manifest={manifest}
          isDisabled={isDisabled}
          entryRequirementsHref={entryRequirementsHref}
          ukCruise={ukCruise}
          i18n={i18n}
        />
        <Divider align="horizontal" />

        {/* <VisumSection i18n={i18n} /> — enable when entryVisa flag is live */}

        <OtherInformationSection
          form={sectionForm}
          manifest={manifest}
          isDisabled={isDisabled}
          i18n={i18n}
        />

        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <p className="text-sm text-marine-medium-emphasis">
            {i18n?.formRequiredNote}
          </p>
          <RadixForm.Submit asChild>
            <Button
              type="submit"
              indicating={isSubmitting}
              disabled={isPreliminary}
              className="w-full md:w-auto"
            >
              {i18n?.formSave}
            </Button>
          </RadixForm.Submit>
        </div>
      </RadixForm.Root>

      {isPinRequired && (
        <ManifestPinDialog
          open
          closeable={false}
          headline={pinDialogHeadline}
          subline={pinDialogSubline}
          onPinEnter={onPinEnter}
          travellerName={fullName}
          i18n={i18n}
        />
      )}

      <AlertDialog
        open={pendingSubmit !== null}
        onOpenChange={open => {
          if (!open) setPendingSubmit(null);
        }}
        actions={[
          { title: i18n?.contactAddressHintCancel ?? 'Bearbeiten' },
          {
            title: i18n?.contactAddressHintConfirm ?? 'Bestätigen',
            primary: true,
            onClick: () => {
              const toSubmit = pendingSubmit;
              setPendingSubmit(null);
              if (toSubmit) onSubmit?.(toSubmit);
            },
          },
        ]}
      >
        {i18n?.contactAddressHintText}
      </AlertDialog>
    </div>
  );
};
